import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../core/client';
import { iotKeys } from '../../core/query-keys';
import { iotEndpoints } from './iot.endpoints';
import type {
  ActuatorState,
  SensorReading,
  SendCommandRequest,
  LocationRef,
  TaggedSensor,
  TaggedActuator,
  SendCommandTarget,
} from './iot.types';

// ── Grow room (parent) ──────────────────────────────────────────────────────────

export function useGrowRoomActuators(roomCode: string) {
  return useQuery({
    queryKey: iotKeys.growRoomActuators(roomCode),
    queryFn: () => api.get<ActuatorState[]>(iotEndpoints.growRoomActuators(roomCode)),
    enabled: Boolean(roomCode),
    refetchInterval: 10_000,
  });
}

export function useGrowRoomSensors(roomCode: string) {
  return useQuery({
    queryKey: iotKeys.growRoomSensors(roomCode),
    queryFn: () => api.get<SensorReading[]>(iotEndpoints.growRoomSensors(roomCode)),
    enabled: Boolean(roomCode),
    refetchInterval: 10_000,
  });
}

export function useSendGrowRoomCommand(roomCode: string, actuator: string) {
  return useMutation({
    mutationFn: (req: SendCommandRequest) =>
      api.post<void>(iotEndpoints.growRoomCommand(roomCode, actuator), req),
  });
}

// ── Grow room rack (child) ──────────────────────────────────────────────────────

export function useGrowRoomRackActuators(roomCode: string, rackCode: string) {
  return useQuery({
    queryKey: iotKeys.growRoomRackActuators(roomCode, rackCode),
    queryFn: () => api.get<ActuatorState[]>(iotEndpoints.growRoomRackActuators(roomCode, rackCode)),
    enabled: Boolean(roomCode) && Boolean(rackCode),
    refetchInterval: 10_000,
  });
}

export function useGrowRoomRackSensors(roomCode: string, rackCode: string) {
  return useQuery({
    queryKey: iotKeys.growRoomRackSensors(roomCode, rackCode),
    queryFn: () => api.get<SensorReading[]>(iotEndpoints.growRoomRackSensors(roomCode, rackCode)),
    enabled: Boolean(roomCode) && Boolean(rackCode),
    refetchInterval: 10_000,
  });
}

export function useSendGrowRoomRackCommand(roomCode: string, rackCode: string, actuator: string) {
  return useMutation({
    mutationFn: (req: SendCommandRequest) =>
      api.post<void>(iotEndpoints.growRoomRackCommand(roomCode, rackCode, actuator), req),
  });
}

// ── Plumbing room (parent) ──────────────────────────────────────────────────────

export function usePlumbingRoomActuators(roomCode: string) {
  return useQuery({
    queryKey: iotKeys.plumbingRoomActuators(roomCode),
    queryFn: () => api.get<ActuatorState[]>(iotEndpoints.plumbingRoomActuators(roomCode)),
    enabled: Boolean(roomCode),
    refetchInterval: 10_000,
  });
}

export function usePlumbingRoomSensors(roomCode: string) {
  return useQuery({
    queryKey: iotKeys.plumbingRoomSensors(roomCode),
    queryFn: () => api.get<SensorReading[]>(iotEndpoints.plumbingRoomSensors(roomCode)),
    enabled: Boolean(roomCode),
    refetchInterval: 10_000,
  });
}

export function useSendPlumbingRoomCommand(roomCode: string, actuator: string) {
  return useMutation({
    mutationFn: (req: SendCommandRequest) =>
      api.post<void>(iotEndpoints.plumbingRoomCommand(roomCode, actuator), req),
  });
}

// ── Plumbing room node (child) ────────────────────────────────────────────────────

export function usePlumbingRoomNodeActuators(roomCode: string, nodeCode: string) {
  return useQuery({
    queryKey: iotKeys.plumbingRoomNodeActuators(roomCode, nodeCode),
    queryFn: () => api.get<ActuatorState[]>(iotEndpoints.plumbingRoomNodeActuators(roomCode, nodeCode)),
    enabled: Boolean(roomCode) && Boolean(nodeCode),
    refetchInterval: 10_000,
  });
}

export function usePlumbingRoomNodeSensors(roomCode: string, nodeCode: string) {
  return useQuery({
    queryKey: iotKeys.plumbingRoomNodeSensors(roomCode, nodeCode),
    queryFn: () => api.get<SensorReading[]>(iotEndpoints.plumbingRoomNodeSensors(roomCode, nodeCode)),
    enabled: Boolean(roomCode) && Boolean(nodeCode),
    refetchInterval: 10_000,
  });
}

export function useSendPlumbingRoomNodeCommand(roomCode: string, nodeCode: string, actuator: string) {
  return useMutation({
    mutationFn: (req: SendCommandRequest) =>
      api.post<void>(iotEndpoints.plumbingRoomNodeCommand(roomCode, nodeCode, actuator), req),
  });
}

// ── Site-wide fan-out (Sensors / Actuators pages) ─────────────────────────────────
// The per-location hooks above address one room/rack at a time. The management pages list
// EVERY channel across the site, so they fan out over the topology with useQueries (one query
// per location) and flatten. ponytail: N location queries polling every 10s — fine for a single
// site (<~50 locations); add a site-wide aggregate GET on the gateway if a site outgrows that.

const sensorEndpoint = (ref: LocationRef): string =>
  ref.kind === 'grow_room_rack'
    ? iotEndpoints.growRoomRackSensors(ref.room, ref.rack!)
    : ref.kind === 'plumbing_room_node'
      ? iotEndpoints.plumbingRoomNodeSensors(ref.room, ref.node!)
      : ref.kind === 'plumbing_room'
        ? iotEndpoints.plumbingRoomSensors(ref.room)
        : iotEndpoints.growRoomSensors(ref.room);

const sensorKey = (ref: LocationRef) =>
  ref.kind === 'grow_room_rack'
    ? iotKeys.growRoomRackSensors(ref.room, ref.rack!)
    : ref.kind === 'plumbing_room_node'
      ? iotKeys.plumbingRoomNodeSensors(ref.room, ref.node!)
      : ref.kind === 'plumbing_room'
        ? iotKeys.plumbingRoomSensors(ref.room)
        : iotKeys.growRoomSensors(ref.room);

const actuatorEndpoint = (ref: LocationRef): string =>
  ref.kind === 'grow_room_rack'
    ? iotEndpoints.growRoomRackActuators(ref.room, ref.rack!)
    : ref.kind === 'plumbing_room_node'
      ? iotEndpoints.plumbingRoomNodeActuators(ref.room, ref.node!)
      : ref.kind === 'plumbing_room'
        ? iotEndpoints.plumbingRoomActuators(ref.room)
        : iotEndpoints.growRoomActuators(ref.room);

const actuatorKey = (ref: LocationRef) =>
  ref.kind === 'grow_room_rack'
    ? iotKeys.growRoomRackActuators(ref.room, ref.rack!)
    : ref.kind === 'plumbing_room_node'
      ? iotKeys.plumbingRoomNodeActuators(ref.room, ref.node!)
      : ref.kind === 'plumbing_room'
        ? iotKeys.plumbingRoomActuators(ref.room)
        : iotKeys.growRoomActuators(ref.room);

const commandEndpoint = (ref: LocationRef, actuator: string): string =>
  ref.kind === 'grow_room_rack'
    ? iotEndpoints.growRoomRackCommand(ref.room, ref.rack!, actuator)
    : ref.kind === 'plumbing_room_node'
      ? iotEndpoints.plumbingRoomNodeCommand(ref.room, ref.node!, actuator)
      : ref.kind === 'plumbing_room'
        ? iotEndpoints.plumbingRoomCommand(ref.room, actuator)
        : iotEndpoints.growRoomCommand(ref.room, actuator);

/** Live sensor readings across every supplied location, tagged with their origin. */
export function useAllSensors(refs: LocationRef[]) {
  const results = useQueries({
    queries: refs.map((ref) => ({
      queryKey: sensorKey(ref),
      queryFn: () => api.get<SensorReading[]>(sensorEndpoint(ref)),
      // One-shot initial snapshot only — live updates arrive over the room
      // WebSocket (R-049, event-driven; no polling). MQTT is the source of truth.
    })),
  });
  const data: TaggedSensor[] = [];
  results.forEach((r, i) => {
    const ref = refs[i];
    if (!ref) return;
    (r.data ?? []).forEach((s) =>
      data.push({ ...s, kind: ref.kind, loc_room: ref.room, loc_rack: ref.rack, loc_node: ref.node }),
    );
  });
  return {
    data,
    // initial load only (all still pending); locations fill in the table as they resolve
    isLoading: results.length > 0 && results.every((r) => r.isLoading),
    isError: results.length > 0 && results.every((r) => r.isError),
    isFetching: results.some((r) => r.isFetching),
    // A location that failed while others answered is otherwise invisible: its rows are simply
    // absent and the table looks complete. The page says how many.
    failedCount: results.filter((r) => r.isError).length,
    refetch: () => Promise.all(results.filter((r) => r.isError).map((r) => r.refetch())),
    /** Every location's snapshot again — a refresh somebody asked for, not just the failed ones. */
    refetchAll: () => Promise.all(results.map((r) => r.refetch())),
  };
}

/** Live actuator states across every supplied location, tagged with their origin. */
export function useAllActuators(refs: LocationRef[]) {
  const results = useQueries({
    queries: refs.map((ref) => ({
      queryKey: actuatorKey(ref),
      queryFn: () => api.get<ActuatorState[]>(actuatorEndpoint(ref)),
      // One-shot initial snapshot only — live actuator state arrives over the
      // room WebSocket (R-049, event-driven; no polling).
    })),
  });
  const data: TaggedActuator[] = [];
  results.forEach((r, i) => {
    const ref = refs[i];
    if (!ref) return;
    (r.data ?? []).forEach((a) =>
      data.push({ ...a, kind: ref.kind, loc_room: ref.room, loc_rack: ref.rack, loc_node: ref.node }),
    );
  });
  return {
    data,
    isLoading: results.length > 0 && results.every((r) => r.isLoading),
    isError: results.length > 0 && results.every((r) => r.isError),
    isFetching: results.some((r) => r.isFetching),
    // As `useAllSensors`: a location that failed while others answered, counted so it can be said.
    failedCount: results.filter((r) => r.isError).length,
    refetch: () => Promise.all(results.filter((r) => r.isError).map((r) => r.refetch())),
    /** Every location's snapshot again — a refresh somebody asked for, not just the failed ones. */
    refetchAll: () => Promise.all(results.map((r) => r.refetch())),
  };
}

/** Generic command dispatch for a multi-row caller (the Actuators table): one hook, any target.
 *  Refreshes that location's actuator snapshot on success so the new state shows without waiting
 *  for the 10s poll. Server enforces role (Manager/Operator) + the level/kind safety gate (D-049). */
export function useSendCommand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ actuator, parameter, ...ref }: SendCommandTarget) =>
      api.post<{ id: string }>(commandEndpoint(ref, actuator), { parameter }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: actuatorKey(vars) });
    },
  });
}
